import React from "react";
import styles from "./ModernTestimonialsSection.module.css";
import heroStyles from "./HeroTestimonials.module.css";
import type { HomepageFeedback } from "./HomeHeroFeedback";

// Institution logos
import KSU from "../assets/workLocations_logos/ksu_logo.webp";
import NGHA from "../assets/workLocations_logos/NGHAlogo.webp";
import KFSH from "../assets/workLocations_logos/KFSHlogo.webp";
import KFMC from "../assets/workLocations_logos/KFMClogo.webp";
import IAFU from "../assets/workLocations_logos/IAFU logo.svg";
import JazanUniversity from "../assets/workLocations_logos/JazanUniversitylogo.svg";
import KSMC from "../assets/workLocations_logos/KSMC logo.svg";
import KAU from "../assets/workLocations_logos/logo_kau.webp";
import MU from "../assets/workLocations_logos/mu_logo.svg";
import PSAU from "../assets/workLocations_logos/PSAU logo.svg";
import TaifUniv from "../assets/workLocations_logos/TaifUniv logo.webp";


// The existing live testimonial card markup, rating renderer and authentic logo map.
// Hero styling is scoped separately; the lower-page carousel remains unchanged.
export default function HeroTestimonialCard({ feedback }: { feedback: HomepageFeedback }) {
  const rating = typeof feedback.rating === "number" && Number.isFinite(feedback.rating) && feedback.rating >= 1 && feedback.rating <= 10 ? feedback.rating : null;
  // Map institution name → logo asset
  const INSTITUTION_LOGOS: Record<string, string> = {
    "King Saud University": KSU,
    "National Guard Health Affairs": NGHA,
    "King Faisal Specialist Hospital": KFSH,
    "King Fahad Medical City": KFMC,
    "Jazan University": JazanUniversity,
    "Taif University": TaifUniv,
    "Majmaah University": MU,
    IAFU: IAFU,
    KSMC: KSMC,
    KAU: KAU,
    PSAU: PSAU,
  };

  // Helper: convert rating (1-10) → array of 5 stars (filled/half/empty)
  const renderStars = (rating: number | null) => {
    if (!rating) return null;
    const starCount = Math.round((rating / 10) * 5 * 10) / 10; // Convert to 0-5 scale
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(starCount)) {
        // Filled star
        stars.push(
          <svg
            key={i}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0L12.2451 6.90983H19.5106L13.6327 11.1803L15.8779 18.0902L10 13.8197L4.12215 18.0902L6.36729 11.1803L0.489435 6.90983H7.75486L10 0Z"
              fill="var(--Colors-Secondary-600)"
            />
          </svg>
        );
      } else if (i - starCount === 0.5) {
        // Half star
        stars.push(
          <svg
            key={i}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0L12.2451 6.90983H19.5106L13.6327 11.1803L15.8779 18.0902L10 13.8197L4.12215 18.0902L6.36729 11.1803L0.489435 6.90983H7.75486L10 0Z"
              fill="var(--Colors-Neutral-300)"
            />
            <path
              d="M10 0L7.75488 6.90979H0.48938L6.36731 11.1803L4.12219 18.0902L10 13.8197V0Z"
              fill="var(--Colors-Secondary-600)"
            />
          </svg>
        );
      } else {
        // Empty star
        stars.push(
          <svg
            key={i}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0L12.2451 6.90983H19.5106L13.6327 11.1803L15.8779 18.0902L10 13.8197L4.12215 18.0902L6.36729 11.1803L0.489435 6.90983H7.75486L10 0Z"
              fill="var(--Colors-Neutral-300)"
            />
          </svg>
        );
      }
    }
    return stars;
  };

  return (
    <div key={feedback.id} className={`${styles.card} ${heroStyles.card}`}>
      {/* Quote icon */}
      <div className={styles.quoteIcon} aria-hidden="true">
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 8C8.34315 8 7 9.34315 7 11V13C7 14.6569 8.34315 16 10 16C11.6569 16 13 14.6569 13 13V11C13 9.34315 11.6569 8 10 8ZM10 18C7.23858 18 5 15.7614 5 13V11C5 8.23858 7.23858 6 10 6C12.7614 6 15 8.23858 15 11V13C15 15.7614 12.7614 18 10 18Z"
            fill="var(--Colors-Primary-300)"
            opacity="0.4"
          />
          <path
            d="M22 8C20.3431 8 19 9.34315 19 11V13C19 14.6569 20.3431 16 22 16C23.6569 16 25 14.6569 25 13V11C25 9.34315 23.6569 8 22 8ZM22 18C19.2386 18 17 15.7614 17 13V11C17 8.23858 19.2386 6 22 6C24.7614 6 27 8.23858 27 11V13C27 15.7614 24.7614 18 22 18Z"
            fill="var(--Colors-Primary-300)"
            opacity="0.4"
          />
        </svg>
      </div>

      {/* Feedback content */}
      <p className={styles.feedbackText}>"{feedback.feedback_content}"</p>

      {/* Rating */}
      {rating !== null && <div className={styles.rating} role="img" aria-label={`${rating / 2} out of 5 stars`}>{renderStars(rating)}</div>}

      {/* Divider */}
      <div className={styles.divider}></div>

      {/* Author info */}
      <div className={styles.author}>
        <div className={styles.authorInfo}>
          <div className={styles.authorName}>
            {feedback.name || "Anonymous"}
          </div>
          {feedback.institution && (
            <div className={styles.authorInstitution}>
              {feedback.institution}
            </div>
          )}
        </div>
        {feedback.institution &&
          INSTITUTION_LOGOS[feedback.institution] && (
            <img
              src={INSTITUTION_LOGOS[feedback.institution]}
              alt={feedback.institution}
              className={styles.institutionLogo}
              loading="eager"
              draggable={false}
            />
          )}
      </div>
    </div>
  );
}
